import { GridCallbackDetails } from '@mui/x-data-grid/models/api/gridCallbackDetails'
import { GridRowId } from '@mui/x-data-grid/models/gridRows'
import { GridRowEditStopParams } from '@mui/x-data-grid/models/params/gridRowParams'
import {
  GridRowEditStopReasons,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid-premium'
import {
  GridColDef,
  MuiEvent,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
  getFontCss,
  HoverableLinkSizes,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useMutationState } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import Button from 'ext-ui/components/Button/Button'
import { spacing } from 'ext-ui/style/spacing'
import _ from 'lodash'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import Box from 'src/components/Box/Box'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import DataGridEditableCell from 'src/components/DataGrid/cells/DataGridEditableCell/DataGridEditableCell'
import TextFieldCell from 'src/components/DataGrid/cells/TextFieldCell/TextFieldCell'
import DataGrid, { DataGridHeaderStyle } from 'src/components/DataGrid/DataGrid'
import FilterSection from 'src/components/FilterSection/FilterSection'
import FilterSectionCheckboxFilter from 'src/components/FilterSectionCheckboxFilter/FilterSectionCheckboxFilter'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgExpand from 'src/components/Svgs/SvgExpand'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { ImageNames } from 'src/enums'
import {
  FormFieldsValidation,
  useDebounceValue,
  useFormFields,
  useStringSetState,
  useWindowSize,
} from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationIntegrationListModel } from 'src/models/organizationIntegration.models'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  ExpenseCategoryLabelFiltersBody,
  ExpenseCategoryLabelResponse,
  ExpenseCategoryLabelSortField,
  PatchExpenseCategoryLabelVariables,
  PostExpenseCategoryLabelRequest,
  usePatchExpenseCategoryLabel,
  usePostExpenseCategoryLabel,
  useQueryExpenseCategory,
  useQueryExpenseCategoryLabels,
} from 'src/queries/organizations/expenseCategories.queries'
import { orgQueryKeys } from 'src/queries/queries.constants'
import { LocationState } from 'src/redux/createRootReducer'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { parseBooleanQueryParamFromSet } from 'src/utils/string.utils'
import {
  stringIsEmpty,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import styled, { css } from 'styled-components'

import styles from './ExpenseCategoryLabelsList.style'

const NEW_EXPENSE_CATEGORY_LABEL_ID = 'NEW_EXPENSE_CATEGORY_LABEL_ID'
const FILTER_SECTION_DATA_GRID_GAP = 20

enum ExpenseCategoryLabelsListColumn {
  LabelName = 'labelName',
  Code = 'code',
  Active = 'active',
  MoreOptions = 'moreOptions',
}

enum LabelStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

interface ExpenseCategoryLabelFilters {
  search?: string
  status?: string[]
}

interface ExpenseCategoryLabelsListProps {
  expenseCategoryId: string
  initialFilterValues: Partial<ExpenseCategoryLabelFilters>
  addingNewLabel: boolean
  onAddingNewLabelChange?: (addingNewLabel: boolean) => void
}

interface ExpenseCategoryLabelsListRow {
  id: string
  label: ExpenseCategoryLabelResponse
  index: number
}

const EmptyStateWrapper = styled.div`
  margin-top: 15%;
  display: flex;
  height: 100%;
`

const ExpenseCategoryLabelsEmptyState: React.FC<{
  activeIntegration: OrganizationIntegrationListModel | undefined
}> = ({ activeIntegration }): React.ReactElement => {
  const translate = useTranslate()
  const listSearchImage = useColorOrGrayscaleImage(ImageNames.ListSearch)

  const { EDIT_EXPENSE_CATEGORIES: hasEditExpCatPermissions } =
    useQueryUserFeatures([UserFeature.EDIT_EXPENSE_CATEGORIES]).data || {}

  return (
    <div
      css={css`
        margin: 40px auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        text-align: center;
        width: 500px;
      `}
    >
      <img width={85} src={listSearchImage} alt={''} />
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
        `}
      >
        {translate(
          hasEditExpCatPermissions
            ? 'Add your labels'
            : "You don't have permission to add labels",
        )}
      </span>
      {activeIntegration !== undefined ? (
        translate('Add labels to your category in ${integrationName}.', {
          integrationName: activeIntegration.platformName,
        })
      ) : (
        <>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              hasEditExpCatPermissions
                ? 'Add labels to your new category by clicking “New Label +” or “Bulk Upload” to import multiple labels at once via CSV. All changes will be automatically saved for you.'
                : "Looks like you don't have access to add labels. In order to get access, contact your org admin.",
            )}
          </span>
          {!hasEditExpCatPermissions && (
            <span
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {translate(
                "You'll still be able to see all labels for this category here.",
              )}
            </span>
          )}
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              'Learn more about Expense Categories in our ${resourceCenter}.',
              {
                resourceCenter: (
                  <ResourceCenterLink
                    size={HoverableLinkSizes.Large}
                    underline
                    regular
                  />
                ),
              },
            )}
          </span>
        </>
      )}
    </div>
  )
}

const NewExpenseCategoryLabelRow = (
  categoryId: string,
): ExpenseCategoryLabelsListRow => ({
  id: NEW_EXPENSE_CATEGORY_LABEL_ID,
  label: {
    id: NEW_EXPENSE_CATEGORY_LABEL_ID,
    name: '',
    code: '',
    active: true,
    categoryId: categoryId,
  },
  index: 0,
})

const ExpenseCategoryLabelsList: React.FC<ExpenseCategoryLabelsListProps> = (
  props,
): React.ReactElement => {
  const history = useHistory<LocationState>()
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()
  const translate = useTranslate()

  // this is fetched in ExpenseCategoryLabels.tsx
  // if this component is re-used elsewhere, it will need to be fetched here
  const activeIntegration = useSelector(selectActiveOrgIntegration)

  const filterSearchText = activeIntegration
    ? 'Search by label name'
    : 'Search by label name or code'
  const filterSectionRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useState<string | undefined>(
    props.initialFilterValues.search,
  )
  const {
    set: statusSet,
    setSet: setStatusSet,
    handleSelection: handleStatusSelection,
  } = useStringSetState(
    new Set<string>(props.initialFilterValues.status) ?? new Set<string>(),
  )

  // query param handlers
  const [updateNewSearchState, setUpdateNewSearchState] =
    useState<boolean>(false)

  const updateUrlStateDebounce = useCallback(
    _.debounce(() => {
      setUpdateNewSearchState(true)
    }, 500),
    [],
  )

  const updateUrlState = (): void => {
    history.replace(
      `?${queryString.stringify({
        search: search || undefined,
        status: Array.from(statusSet),
      })}`,
    )
  }

  useEffect((): void => {
    if (updateNewSearchState) {
      updateUrlState()
      setUpdateNewSearchState(false)
    }
  }, [updateNewSearchState])

  // filters logic
  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (statusSet.size > 0) {
      appliedCount++
    }
    return appliedCount
  }

  const resetFilters = (): void => {
    setSearch(undefined)
    setStatusSet(new Set())
    updateUrlStateDebounce()
  }

  const filters: ExpenseCategoryLabelFiltersBody = useMemo(
    () => ({
      search: search,
      active: parseBooleanQueryParamFromSet(statusSet, LabelStatus.Active),
      // TODO: allow for changing sort order
      sortField: ExpenseCategoryLabelSortField.Name,
      sortDirection: 'ASC',
    }),
    [search, statusSet],
  )

  const reactQueryFilters = useDebounceValue(filters, 300)

  const isDirty =
    filtersAppliedCount() > 0 ||
    (reactQueryFilters.search !== undefined &&
      reactQueryFilters.search.length > 0)

  const {
    data: labels,
    fetchNextPage,
    isFetching,
    complete,
  } = useQueryExpenseCategoryLabels({
    expenseCategoryId: props.expenseCategoryId,
    filters: reactQueryFilters,
  })
  const { data: expenseCategory } = useQueryExpenseCategory({
    expenseCategoryId: props.expenseCategoryId,
  })

  const { EDIT_EXPENSE_CATEGORIES: hasEditExpCatPermissions } =
    useQueryUserFeatures([UserFeature.EDIT_EXPENSE_CATEGORIES]).data || {}

  const { mutateAsync: patchLabel, isPending: patchLabelLoading } =
    usePatchExpenseCategoryLabel()
  const patchExpenseCategoryLabelPendingMutations = useMutationState({
    filters: { mutationKey: orgQueryKeys.patchExpenseDataLabel() },
    // TS can't know the type just from the mutationKey
    // https://github.com/TanStack/query/issues/6843#issuecomment-1941577758
    select: (mutation) =>
      mutation.state.status === 'pending'
        ? (mutation.state.variables as
            | PatchExpenseCategoryLabelVariables
            | undefined)
        : undefined,
  }).filter(
    (variables): variables is PatchExpenseCategoryLabelVariables => !!variables,
  )

  const { mutateAsync: postNewLabel, isPending: postNewLabelLoading } =
    usePostExpenseCategoryLabel()

  const fieldLoading = patchLabelLoading || postNewLabelLoading

  const [windowWidth, windowHeight] = useWindowSize()

  const tableHeight = useMemo(() => {
    const filterSectionHeight = filterSectionRef?.current?.clientHeight ?? 0
    const sectionHeight = sectionRef?.current?.clientHeight ?? 0
    return sectionHeight - filterSectionHeight + FILTER_SECTION_DATA_GRID_GAP
  }, [sectionRef.current, filterSectionRef.current, windowWidth, windowHeight])

  // TODO: move all this logic for adding a new row into a reusable implementation
  // adding label logic
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  const handleRowModesModelChange = useCallback(
    (newRowModesModel: GridRowModesModel, _: GridCallbackDetails): void => {
      // handle edit status of new label row separately
      delete newRowModesModel[NEW_EXPENSE_CATEGORY_LABEL_ID]
      setRowModesModel((old) => ({ ...old, ...newRowModesModel }))
    },
    [setRowModesModel],
  )

  const processRowUpdate = async (
    // if a cell has a new value in a row update, it is added to the newRow object
    newRow: ExpenseCategoryLabelsListRow & {
      [ExpenseCategoryLabelsListColumn.LabelName]?: string
      [ExpenseCategoryLabelsListColumn.Code]?: string
    },
    _: ExpenseCategoryLabelsListRow,
    params: { rowId: GridRowId },
  ): Promise<ExpenseCategoryLabelsListRow> => {
    // only existing row updates are handled via processRowUpdate
    if (params.rowId === NEW_EXPENSE_CATEGORY_LABEL_ID) return newRow

    if (props.expenseCategoryId !== undefined) {
      if (hasLabelErrors || !setErrorsAndReturnDidPassValidation())
        return Promise.reject('FRONT_END_VALIDATION')
      // if promise is rejected, it will trigger onProcessRowUpdateError without updating the row
      return patchLabel({
        categoryId: props.expenseCategoryId,
        labelId: newRow.id,
        body: { name: newRow.labelName },
      }).then(() => newRow)
    }
    // default fallback
    return Promise.resolve(newRow)
  }

  const onProcessRowUpdateError = useCallback(
    (e: string | AxiosError<BackendErrorPayload<string>>): void => {
      // handle case where setErrorsAndReturnDidPassValidation already handled setting front-end validation errors
      if (typeof e === 'string') return

      // handle back-end validation errors
      if (props.addingNewLabel) {
        // backend error goes on code field when adding new label
        setFieldsErrors({ code: [e.response?.data.error ?? e.message] })
      } else {
        // labelName is the only cell available when not adding a new label
        setFieldsErrors({ name: [e.response?.data.error ?? e.message] })
      }
    },
    [],
  )

  // hide new label row whenever not adding new label
  const handleRowEditStart = (): void => {
    if (props.addingNewLabel) {
      props.onAddingNewLabelChange?.(false)
    }
  }

  // new label row does not go through processRowUpdate the same way because it is manipulated via the DataGrid api
  // so we handle it separately here
  const handleRowEditStop = (
    params: GridRowEditStopParams<ExpenseCategoryLabelsListRow>,
    event: MuiEvent,
  ): void => {
    /**
     * If a row is not visible when the user moves from editing one cell to another (i.e. from scrolling to another part
     * of the table, resulting in the previous row being optimized a way via virtualization) then instead of exiting
     * edit mode in the usual way it triggers a handleRowEditStop event with reason 'rowFocusOut'
     *
     * this catches all cases where the user is not trying to save the row by pressing enter and prevents the default
     * behaviour of exiting edit mode and saving changes
     */
    if (params.reason !== GridRowEditStopReasons.enterKeyDown) {
      event.defaultMuiPrevented = true
      apiRef.current?.stopRowEditMode({
        id: params.id,
        ignoreModifications: true,
      })
    }

    // only handle new label case here
    if (params.id !== NEW_EXPENSE_CATEGORY_LABEL_ID) return

    event.defaultMuiPrevented = true

    if (params.reason === GridRowEditStopReasons.enterKeyDown) {
      if (hasLabelErrors || !setErrorsAndReturnDidPassValidation()) return
      if (props.expenseCategoryId !== undefined) {
        postNewLabel({
          categoryId: props.expenseCategoryId,
          body: { name: fields.name, code: fields.code, active: true },
        })
          .then(() => {
            setFields({ name: '', code: '' })
            apiRef.current?.setEditCellValue({
              id: NEW_EXPENSE_CATEGORY_LABEL_ID,
              field: ExpenseCategoryLabelsListColumn.LabelName,
              value: '',
            })
            apiRef.current?.setEditCellValue({
              id: NEW_EXPENSE_CATEGORY_LABEL_ID,
              field: ExpenseCategoryLabelsListColumn.Code,
              value: '',
            })
          })
          .catch((e: AxiosError<BackendErrorPayload<string>>) => {
            setFieldsErrors({ code: [e.response?.data.error ?? e.message] })
          })
          .finally(() => {
            apiRef.current?.setCellFocus(
              NEW_EXPENSE_CATEGORY_LABEL_ID,
              ExpenseCategoryLabelsListColumn.LabelName,
            )
          })
      }
    } else {
      props.onAddingNewLabelChange?.(false)
    }
  }

  const {
    fields,
    setFields,
    fieldsErrors: labelErrors,
    hasFieldsErrors: hasLabelErrors,
    setFieldsErrors,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<PostExpenseCategoryLabelRequest>(
    {
      name: '',
      code: '',
    },
    (): FormFieldsValidation<PostExpenseCategoryLabelRequest> => ({
      name: [stringIsEmpty(), stringLengthIsLessThan(255)],
      code: props.addingNewLabel
        ? [stringIsEmpty(), stringLengthIsLessThan(255)]
        : [],
    }),
  )

  // manually handle new label row's edit status
  useEffect(() => {
    if (props.addingNewLabel) {
      setRowModesModel((currentRowModesModel) => ({
        ...currentRowModesModel,
        [NEW_EXPENSE_CATEGORY_LABEL_ID]: { mode: GridRowModes.Edit },
      }))
      apiRef.current?.setCellFocus(
        NEW_EXPENSE_CATEGORY_LABEL_ID,
        ExpenseCategoryLabelsListColumn.LabelName,
      )
    } else {
      setRowModesModel((currentRowModesModel) => ({
        ...currentRowModesModel,
        [NEW_EXPENSE_CATEGORY_LABEL_ID]: {
          mode: GridRowModes.View,
          ignoreModifications: true,
        },
      }))
      setFields({ name: '', code: '' })
      setFieldsErrors({})
    }
  }, [props.addingNewLabel])

  const onActiveSwitchChange = useCallback(
    async (labelId: string, active: boolean): Promise<boolean> => {
      if (props.expenseCategoryId === undefined) return false
      return await patchLabel({
        categoryId: props.expenseCategoryId,
        labelId,
        body: { active },
      })
        .then(() => true)
        .catch((e: AxiosError<BackendErrorPayload<string>>) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <BackendErrorToastMessage error={e} />,
              failureColorScheme: true,
              hideImage: true,
            }),
          )
          return false
        })
    },
    [props.expenseCategoryId],
  )

  // new label row is always first, dependent on addingNewLabel
  const rows = useMemo(() => {
    return [
      props.addingNewLabel
        ? NewExpenseCategoryLabelRow(props.expenseCategoryId)
        : undefined,
      ...(labels ?? []).map((label, i) => {
        return {
          id: label.id,
          label: label,
          index: i + 1,
        }
      }),
    ].filter((row): row is ExpenseCategoryLabelsListRow => !!row)
  }, [labels, props.addingNewLabel])

  const columns = useMemo(() => {
    const cols: Array<GridColDef<ExpenseCategoryLabelsListRow, string>> = [
      {
        field: ExpenseCategoryLabelsListColumn.LabelName,
        valueGetter: (_, row): string => row.label.name,
        headerName: translate('Label Name'),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: expenseCategory?.editable && hasEditExpCatPermissions,
        flex: 1,
        minWidth: 100,
        renderCell: (params): React.ReactElement => {
          const label = params.row.label
          return (
            <DataGridEditableCell
              tooltipContent={
                expenseCategory?.editable && hasEditExpCatPermissions
                  ? translate('Edit label name')
                  : translate('Cannot edit name')
              }
              params={params}
              editMode={'row'}
              value={label.name}
            />
          )
        },
        renderEditCell: (params): React.ReactElement => {
          return (
            <TextFieldCell
              characterLimit={255}
              tooltip={translate('Edit label name')}
              placeholder={translate('Enter label name')}
              params={params}
              editMode={'row'}
              microcopy={
                params.row.id === NEW_EXPENSE_CATEGORY_LABEL_ID
                  ? undefined
                  : translate('Press Enter to save or Esc to cancel')
              }
              disabled={
                patchExpenseCategoryLabelPendingMutations.some(
                  (variables) => variables.labelId === params.row.id,
                ) || postNewLabelLoading
              }
              errorText={labelErrors.name?.[0]}
              clearError={(): void =>
                setFieldsErrors({ ...labelErrors, name: undefined })
              }
              setValue={(value): void => setFields({ ...fields, name: value })}
            />
          )
        },
      },
      {
        field: ExpenseCategoryLabelsListColumn.Code,
        headerName: translate('Code'),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: hasEditExpCatPermissions,
        flex: 1,
        minWidth: 100,
        renderCell: (params): React.ReactElement => {
          const label = params.row.label
          return <DataGridCell params={params} text={label.code} />
        },
        renderEditCell: (params): React.ReactElement => {
          return (
            <TextFieldCell
              characterLimit={255}
              tooltip={translate('Edit code')}
              placeholder={translate('Enter code')}
              params={params}
              editMode={'row'}
              microcopy={translate('Press Enter to save or Esc to cancel')}
              disabled={
                patchExpenseCategoryLabelPendingMutations.some(
                  (variables) => variables.labelId === params.row.id,
                ) || postNewLabelLoading
              }
              errorText={labelErrors.code?.[0]}
              clearError={(): void =>
                setFieldsErrors({ ...labelErrors, code: undefined })
              }
              setValue={(value): void => setFields({ ...fields, code: value })}
            />
          )
        },
      },
      {
        field: ExpenseCategoryLabelsListColumn.Active,
        description: translate(
          'When active, everyone in the org can code transactions to the label. When inactive, the label will be hidden from view.',
        ),
        headerName: translate('Active'),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: false,
        minWidth: 115,
        renderCell: (params): React.ReactElement => {
          // handle new label row
          if (params.row.id === NEW_EXPENSE_CATEGORY_LABEL_ID) return <></>

          const label = params.row.label
          return (
            <DataGridCellWrapper params={params}>
              <OnOffSwitch
                testId={'isActiveSwitch'}
                smallMode
                defaultOnOffState={label.active}
                onChange={(): Promise<boolean> =>
                  onActiveSwitchChange(label.id, !label.active)
                }
                disabled={
                  !hasEditExpCatPermissions ||
                  patchExpenseCategoryLabelPendingMutations.some(
                    (variables) => variables.labelId === label.id,
                  )
                }
              />
            </DataGridCellWrapper>
          )
        },
      },
      {
        field: ExpenseCategoryLabelsListColumn.MoreOptions,
        type: 'custom',
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        hideable: false,
        maxWidth: 72,
        headerClassName: 'hideRightSeparator',
        renderCell: (params): React.ReactElement => {
          const api = useGridApiContext()
          const isEditing =
            api.current?.getRowMode(params.id) === GridRowModes.Edit

          if (
            patchExpenseCategoryLabelPendingMutations.some(
              (variables) => variables.labelId === params.row.id,
            ) &&
            isEditing
          ) {
            return (
              <DataGridCellWrapper params={params}>
                <Processing />
              </DataGridCellWrapper>
            )
          } else {
            // TODO: add more options when needed
            return <></>
          }
        },
        renderHeader: () => <></>,
      },
    ]
    if (activeIntegration !== undefined) {
      return cols.filter(
        (col) => col.field !== ExpenseCategoryLabelsListColumn.Code,
      )
    } else {
      return cols
    }
  }, [activeIntegration, labelErrors, fieldLoading, hasEditExpCatPermissions])

  return (
    <div className="ExpenseCategoryLabelsList" css={styles} ref={sectionRef}>
      <Box
        noPadding
        noShadow
        outlineBorder
        css={css`
          overflow: hidden;
        `}
      >
        <div className={'table-header'}>
          <div className={'toolbar-container'}>
            <KeywordFilter
              css={css`
                flex: 1;
              `}
              value={search || ''}
              onChange={(val: string): void => {
                setSearch(val)
                updateUrlStateDebounce()
              }}
              placeholder={translate(filterSearchText)}
            />
            <div className={'flex-row gap-3'}>
              <Button
                variant={'toolbar'}
                dropShadow={false}
                iconLeft={
                  <SvgExpand width={16} height={16} viewBox={'0 0 24 24'} />
                }
                onClick={(): void => {
                  props.onAddingNewLabelChange?.(true)
                }}
                disabled={props.addingNewLabel || !hasEditExpCatPermissions}
                data-testid="add-label-btn"
              >
                {translate('Add Label')}
              </Button>
              <FilterSection
                innerRef={filterSectionRef}
                appliedFiltersCount={filtersAppliedCount()}
                filtersFlyoutProps={{
                  title: translate('Label Filters'),
                  onReset: resetFilters,
                  loading: false,
                  filters: (
                    <>
                      <FilterSectionCheckboxFilter
                        headerSource={'Label Status'}
                        checkboxProps={[
                          {
                            labelSource: 'Active',
                            onChange: (): void => {
                              handleStatusSelection(LabelStatus.Active)
                              updateUrlStateDebounce()
                            },
                            checked: statusSet.has(LabelStatus.Active),
                          },
                          {
                            labelSource: 'Inactive',
                            onChange: (): void => {
                              handleStatusSelection(LabelStatus.Inactive)
                              updateUrlStateDebounce()
                            },
                            checked: statusSet.has(LabelStatus.Inactive),
                          },
                        ]}
                      />
                    </>
                  ),
                }}
                hideButtonChevron
                removePadding
                buttonVariant={'toolbarIcon'}
              />
            </div>
          </div>
        </div>
        <DataGrid
          apiRef={apiRef}
          headerStyle={DataGridHeaderStyle.NoBackground}
          height={tableHeight}
          rows={rows}
          columns={columns}
          filtersBody={filters}
          disableColumnFilter
          infiniteScrollProps={{
            fetch: () => fetchNextPage(),
            loading: isFetching,
            complete: complete,
          }}
          slots={{
            noRowsOverlay: () => (
              <EmptyStateWrapper>
                {isDirty ? (
                  <TableEmptyState
                    design={TableEmptyStateDesigns.ExpenseCategoryLabels}
                  />
                ) : (
                  <ExpenseCategoryLabelsEmptyState
                    activeIntegration={activeIntegration}
                  />
                )}
              </EmptyStateWrapper>
            ),
          }}
          // TODO: implement when bulk actions are needed
          // checkboxSelection={true}
          isCellEditable={(params): boolean => {
            return (
              (params.colDef.field === ExpenseCategoryLabelsListColumn.Code &&
                params.row.id === NEW_EXPENSE_CATEGORY_LABEL_ID) ||
              params.colDef.field === ExpenseCategoryLabelsListColumn.LabelName
            )
          }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onProcessRowUpdateError}
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          onRowEditStart={handleRowEditStart}
          rowSelection={false}
          hideFooterRowCount
          sx={{
            '.MuiDataGrid-cell': {
              padding: `${spacing(2)} 0`,
            },
            '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
              display: 'flex',
              alignItems: 'flex-start',
              padding: `${spacing(2)} 0`,
            },
            '.MuiDataGrid-cell .MuiDataGrid-actionsCell': {
              padding: 0,
              gridGap: 0,
            },
          }}
        />
      </Box>
    </div>
  )
}

export default ExpenseCategoryLabelsList
